import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const ActiveModalContext = createContext({
  activeModal: null,
  registerModal: () => () => {},
  clearModals: () => {}
});

/**
 * Empilha modais abertos para o reporte de erros capturar o contexto
 * (ex.: "Consumir Ingredientes", "Novo recebimento").
 */
export function ActiveModalProvider({ children }) {
  const [stack, setStack] = useState([]);

  const registerModal = useCallback((modal) => {
    if (!modal || !(modal.title || modal.id)) return () => {};
    const key = `${modal.id || 'modal'}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const entry = {
      key,
      id: String(modal.id || ''),
      title: String(modal.title || '').trim().slice(0, 200),
      extra: modal.extra || null
    };
    setStack((prev) => [...prev, entry]);
    return () => {
      setStack((prev) => prev.filter((m) => m.key !== key));
    };
  }, []);

  const clearModals = useCallback(() => setStack([]), []);

  const activeModal = stack.length > 0 ? stack[stack.length - 1] : null;

  const value = useMemo(
    () => ({ activeModal, registerModal, clearModals, stack }),
    [activeModal, registerModal, clearModals, stack]
  );

  return <ActiveModalContext.Provider value={value}>{children}</ActiveModalContext.Provider>;
}

export function useActiveModal() {
  return useContext(ActiveModalContext);
}

/**
 * Registra o modal enquanto `isOpen` for true.
 * @param {boolean} isOpen
 * @param {{ id?: string, title: string, extra?: object } | null} info
 */
export function useRegisterActiveModal(isOpen, info) {
  const { registerModal } = useActiveModal();
  const title = info?.title || '';
  const id = info?.id || '';

  useEffect(() => {
    if (!isOpen || !title) return undefined;
    return registerModal({ id, title, extra: info?.extra });
  }, [isOpen, id, title, registerModal]); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Detecta modal visível no DOM (fallback quando a página não registrou via contexto).
 * Ignora o próprio diálogo de reporte.
 */
export function detectarModalAtivoNoDom() {
  if (typeof document === 'undefined') return null;

  const isVisible = (el) => {
    if (!el || !(el instanceof HTMLElement)) return false;
    if (el.closest('[data-report-issue-root="true"]')) return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
      return false;
    }
    const rect = el.getBoundingClientRect();
    return rect.width > 40 && rect.height > 40;
  };

  const extractTitle = (root) => {
    if (!root) return '';
    const labelledBy = root.getAttribute?.('aria-labelledby');
    if (labelledBy) {
      const labelEl = document.getElementById(labelledBy);
      const t = labelEl?.textContent?.trim();
      if (t) return t.slice(0, 200);
    }
    const heading = root.querySelector?.(
      'h1, h2, h3, [data-modal-title], .font-semibold, .font-bold'
    );
    const t = heading?.textContent?.trim();
    if (t) return t.replace(/\s+/g, ' ').slice(0, 200);
    return '';
  };

  const scored = [];

  document.querySelectorAll('[role="dialog"], [aria-modal="true"]').forEach((el) => {
    if (!isVisible(el)) return;
    const z = parseInt(window.getComputedStyle(el).zIndex, 10) || 0;
    scored.push({ el, z, title: extractTitle(el) });
  });

  // Padrão comum no app: overlay fixed inset-0 com painel branco
  document.querySelectorAll('.fixed.inset-0').forEach((el) => {
    if (!isVisible(el)) return;
    if (el.getAttribute('data-report-issue-root') === 'true') return;
    // Preferir o painel interno se houver
    const panel =
      el.querySelector('.bg-white, [class*="bg-white"], [role="dialog"]') || el;
    const z = parseInt(window.getComputedStyle(el).zIndex, 10) || 0;
    const title = extractTitle(panel) || extractTitle(el);
    if (!title && z < 40) return;
    scored.push({ el: panel, z, title });
  });

  if (scored.length === 0) return null;

  scored.sort((a, b) => a.z - b.z);
  const top = scored[scored.length - 1];
  const title = (top.title || '').trim();
  if (!title) return null;
  // Evitar capturar o próprio botão/report se algo vazou
  if (/reportar erro ou ajuste/i.test(title)) return null;

  return {
    id: 'dom-detected',
    title,
    extra: { source: 'dom' }
  };
}
